// src/components/BoardView.tsx
import React, { useState } from 'react';
import { tasksApi, Task } from '../api/tasks';
import TaskCard from './TaskCard';
import { usePermissionGuard } from '../hooks/usePermissionGuard';

interface BoardViewProps {
  tasks: Task[];
  onRefresh: () => void;
}

const statusColumns = [
  { status: 'TODO', label: 'To Do', icon: '📋', color: '#6B7280', bg: '#F3F4F6' },
  { status: 'IN_PROGRESS', label: 'In Progress', icon: '⚙️', color: '#2563EB', bg: '#EFF6FF' },
  { status: 'IN_REVIEW', label: 'In Review', icon: '🔍', color: '#7C3AED', bg: '#F5F3FF' },
  { status: 'BLOCKED', label: 'Blocked', icon: '🚫', color: '#DC2626', bg: '#FEF2F2' },
  { status: 'DONE', label: 'Done', icon: '✅', color: '#059669', bg: '#ECFDF5' },
];

export function BoardView({ tasks, onRefresh }: BoardViewProps) {
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { showError, showSuccess } = usePermissionGuard();

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    setDraggedTaskId(taskId);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
  };

  const handleDragEnd = () => {
    setDraggedTaskId(null);
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain');
    
    if (!taskId) return;
    
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    
    if (task.status === newStatus) {
      setIsDragging(false);
      return;
    }

    try {
      await tasksApi.changeStatus(taskId, { status: newStatus });
      showSuccess('Task Moved', `Task moved to ${statusColumns.find(c => c.status === newStatus)?.label}`);
      onRefresh();
    } catch (err: any) {
      showError('MOVE_FAILED', err.message || 'Failed to move task');
    } finally {
      setIsDragging(false);
      setDraggedTaskId(null);
    }
  };

  const getColumnTasks = (status: string) => {
    return tasks.filter(task => task.status === status);
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 min-h-[500px]">
      {statusColumns.map((column) => {
        const columnTasks = getColumnTasks(column.status);
        
        return (
          <div
            key={column.status}
            className="flex-shrink-0 w-80 bg-gray-50 rounded-xl p-3 flex flex-col"
            style={{ minHeight: '400px' }}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, column.status)}
          >
            {/* Column Header */}
            <div
              className="flex items-center justify-between mb-3 px-3 py-2 rounded-lg"
              style={{ backgroundColor: column.bg }}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{column.icon}</span>
                <span className="font-semibold text-sm" style={{ color: column.color }}>
                  {column.label}
                </span>
              </div>
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full bg-white"
                style={{ color: column.color }}
              >
                {columnTasks.length}
              </span>
            </div>

            {/* Column Tasks */}
            <div className="flex-1 space-y-3 overflow-y-auto max-h-[calc(100vh-300px)]">
              {columnTasks.map((task) => (
                <div
                  key={task.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, task.id)}
                  onDragEnd={handleDragEnd}
                  className={`cursor-grab active:cursor-grabbing transition-all duration-200 ${
                    draggedTaskId === task.id ? 'opacity-50 scale-95' : ''
                  }`}
                >
                  <TaskCard task={task} onRefresh={onRefresh} />
                </div>
              ))}
              
              {columnTasks.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg">
                  Drop tasks here
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}